import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReuseAvailability } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailability';
import { Flex, Checkbox, InputRange, Text } from 'UI';
import { textFormatter } from 'utils';
import { SColorLabel, SColorRow, SColorSwatch, SFiltersColumn, SFilterSection } from './ReuseAvailabilities.styled';
import { parseReuseNetPrice } from './reusePricing';

// Opisto reuse-availabilities feature is gated to France only — currency is always EUR.
const REUSE_CURRENCY = 'EUR';

export type ReuseDeliveryOptionId = 'FREE' | 'UNDER_24H' | 'UNDER_48H' | 'UNDER_72H';
export type ReuseConditionOptionId = 'Good' | 'Correct' | 'Bad';

export interface ReuseFilters {
  delivery: ReuseDeliveryOptionId[];
  price: [number, number];
  // Colour codes (see COLOR_OPTIONS.code), not supplier labels.
  colors: string[];
  conditions: ReuseConditionOptionId[];
}

const DELIVERY_OPTIONS: { id: ReuseDeliveryOptionId; labelKey: string; labelDefault: string }[] = [
  { id: 'FREE', labelKey: 'catalog.reuse_availabilities.filters.delivery.free', labelDefault: 'Free delivery' },
  { id: 'UNDER_24H', labelKey: 'catalog.reuse_availabilities.filters.delivery.under_24h', labelDefault: 'Under 24h' },
  { id: 'UNDER_48H', labelKey: 'catalog.reuse_availabilities.filters.delivery.under_48h', labelDefault: 'Under 48h' },
  { id: 'UNDER_72H', labelKey: 'catalog.reuse_availabilities.filters.delivery.under_72h', labelDefault: 'Under 72h' },
];

const CONDITION_OPTIONS: { id: ReuseConditionOptionId; labelKey: string; labelDefault: string }[] = [
  { id: 'Good', labelKey: 'catalog.reuse_availabilities.filters.condition.good', labelDefault: 'Good' },
  { id: 'Correct', labelKey: 'catalog.reuse_availabilities.filters.condition.correct', labelDefault: 'Correct' },
  { id: 'Bad', labelKey: 'catalog.reuse_availabilities.filters.condition.bad', labelDefault: 'Bad' },
];

// Static swatch list shown in full regardless of the loaded data (per spec).
// `code` is both the swatch colour and the filter value - see resolveReuseColorCode.
const COLOR_OPTIONS: { id: string; labelKey: string; labelDefault: string; code: string }[] = [
  {
    id: 'BLACK',
    labelKey: 'catalog.reuse_availabilities.filters.color.black',
    labelDefault: 'Black',
    code: '#000000',
  },
  { id: 'BLUE', labelKey: 'catalog.reuse_availabilities.filters.color.blue', labelDefault: 'Blue', code: '#2f6bff' },
  {
    id: 'DARK_BLUE',
    labelKey: 'catalog.reuse_availabilities.filters.color.dark_blue',
    labelDefault: 'Dark Blue',
    code: '#0a1a4f',
  },
  {
    id: 'BROWN_CHAMPAGNE',
    labelKey: 'catalog.reuse_availabilities.filters.color.brown_champagne',
    labelDefault: 'Brown Champagne',
    code: '#a68a6d',
  },
  {
    id: 'WHITE',
    labelKey: 'catalog.reuse_availabilities.filters.color.white',
    labelDefault: 'White',
    code: '#ffffff',
  },
  {
    id: 'GREEN',
    labelKey: 'catalog.reuse_availabilities.filters.color.green',
    labelDefault: 'Green',
    code: '#1aa03c',
  },
  { id: 'GREY', labelKey: 'catalog.reuse_availabilities.filters.color.grey', labelDefault: 'Grey', code: '#6b6b6b' },
  { id: 'RED', labelKey: 'catalog.reuse_availabilities.filters.color.red', labelDefault: 'Red', code: '#d0021b' },
  {
    id: 'ORANGE',
    labelKey: 'catalog.reuse_availabilities.filters.color.orange',
    labelDefault: 'Orange',
    code: '#f5a623',
  },
  {
    id: 'YELLOW',
    labelKey: 'catalog.reuse_availabilities.filters.color.yellow',
    labelDefault: 'Yellow',
    code: '#f8e71c',
  },
];

// Opisto currently exposes only a localised colour label (FR), not a colour code. Until it sends
// `colorCode`, the label is resolved to a code here and filtering is done on the code, so switching
// to the supplier code later is a change in this one function.
const COLOR_LABEL_TO_CODE: Record<string, string> = {
  NOIR: '#000000',
  BLACK: '#000000',
  BLEU: '#2f6bff',
  BLUE: '#2f6bff',
  BLEU_FONCE: '#0a1a4f',
  DARK_BLUE: '#0a1a4f',
  MARRON: '#a68a6d',
  BEIGE: '#a68a6d',
  CHAMPAGNE: '#a68a6d',
  BROWN: '#a68a6d',
  BLANC: '#ffffff',
  WHITE: '#ffffff',
  VERT: '#1aa03c',
  GREEN: '#1aa03c',
  GRIS: '#6b6b6b',
  GREY: '#6b6b6b',
  GRAY: '#6b6b6b',
  ROUGE: '#d0021b',
  RED: '#d0021b',
  ORANGE: '#f5a623',
  JAUNE: '#f8e71c',
  YELLOW: '#f8e71c',
};

export const resolveReuseColorCode = (color: string | undefined | null): string | undefined => {
  if (color == null) {
    return undefined;
  }
  const key = color
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return COLOR_LABEL_TO_CODE[key];
};

const isPresent = (value: string | number | undefined | null): boolean =>
  value !== undefined && value !== null && value !== '';

const matchesDelivery = (part: ReuseAvailability, id: ReuseDeliveryOptionId): boolean => {
  switch (id) {
    case 'FREE':
      return isPresent(part.deliveryFee) && Number(part.deliveryFee) === 0;
    case 'UNDER_24H':
      return part.shippingDelayMax === 1;
    case 'UNDER_48H':
      return part.shippingDelayMax === 2;
    case 'UNDER_72H':
      return part.shippingDelayMax === 3;
    default:
      return true;
  }
};

const matchesCondition = (partCondition: string, conditionId: ReuseConditionOptionId): boolean =>
  partCondition.trim().toUpperCase() === conditionId.toUpperCase();

// Rounded [min, max] net-price bounds across the currently loaded parts.
export const getReusePriceBounds = (parts: ReuseAvailability[]): [number, number] => {
  const prices = parts
    .map((part) => parseReuseNetPrice(part.netPrice))
    .filter((price): price is number => price !== undefined);
  if (prices.length === 0) {
    return [0, 0];
  }
  return [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))];
};

export const defaultReuseFilters = (parts: ReuseAvailability[]): ReuseFilters => ({
  delivery: [],
  price: getReusePriceBounds(parts),
  colors: [],
  conditions: [],
});

export const applyReuseFilters = (parts: ReuseAvailability[], filters: ReuseFilters): ReuseAvailability[] =>
  parts.filter((part) => {
    const deliveryOk = filters.delivery.length === 0 || filters.delivery.some((id) => matchesDelivery(part, id));
    const price = parseReuseNetPrice(part.netPrice);
    const priceOk = price === undefined || (price >= filters.price[0] && price <= filters.price[1]);
    const partColorCode = resolveReuseColorCode(part.color);
    const colorOk =
      filters.colors.length === 0 || (partColorCode !== undefined && filters.colors.includes(partColorCode));
    const conditionOk =
      filters.conditions.length === 0 ||
      (part.condition != null && filters.conditions.some((id) => matchesCondition(part.condition as string, id)));
    return deliveryOk && priceOk && colorOk && conditionOk;
  });

interface ReuseAvailabilitiesFiltersProps {
  filters: ReuseFilters;
  onFiltersChange: (filters: ReuseFilters) => void;
  priceBounds: [number, number];
}

const ReuseAvailabilitiesFilters = ({ filters, onFiltersChange, priceBounds }: ReuseAvailabilitiesFiltersProps) => {
  const { t } = useTranslation();

  const [minBound, maxBound] = priceBounds;
  const priceDisabled = minBound >= maxBound;

  const toggleDelivery = (id: ReuseDeliveryOptionId) => {
    const delivery = filters.delivery.includes(id)
      ? filters.delivery.filter((item) => item !== id)
      : [...filters.delivery, id];
    onFiltersChange({ ...filters, delivery });
  };

  const toggleColor = (id: string) => {
    const colors = filters.colors.includes(id) ? filters.colors.filter((item) => item !== id) : [...filters.colors, id];
    onFiltersChange({ ...filters, colors });
  };

  const toggleCondition = (id: ReuseConditionOptionId) => {
    const conditions = filters.conditions.includes(id)
      ? filters.conditions.filter((item) => item !== id)
      : [...filters.conditions, id];
    onFiltersChange({ ...filters, conditions });
  };

  return (
    <SFiltersColumn data-testid={'reuse-availabilities-filters'}>
      <SFilterSection>
        <Text type={'base'} bold>
          {t('catalog.reuse_availabilities.filters.delivery', 'Delivery')}
        </Text>
        <Flex direction={'column'} gap={8}>
          {DELIVERY_OPTIONS.map((option) => (
            <Checkbox
              key={option.id}
              type={'base'}
              checked={filters.delivery.includes(option.id)}
              onChange={() => toggleDelivery(option.id)}
              label={t(option.labelKey, option.labelDefault)}
              data-testid={`reuse-filter-delivery-${option.id.toLowerCase()}`}
              data-cy={`reuse-filter-delivery-${option.id.toLowerCase()}`}
            />
          ))}
        </Flex>
      </SFilterSection>
      <SFilterSection>
        <Text type={'base'} bold>
          {t('catalog.reuse_availabilities.filters.price', 'Price')}
        </Text>
        <InputRange
          min={minBound}
          max={maxBound}
          value={filters.price}
          disabled={priceDisabled}
          tipFormatter={(value) => textFormatter.formatCurrency(value ?? 0, REUSE_CURRENCY)}
          onChange={(price) => onFiltersChange({ ...filters, price })}
        />
      </SFilterSection>

      <SFilterSection>
        <Text type={'base'} bold>
          {t('catalog.reuse_availabilities.filters.condition', 'Parts condition')}
        </Text>
        <Flex direction={'column'} gap={8}>
          {CONDITION_OPTIONS.map((option) => (
            <Checkbox
              key={option.id}
              type={'base'}
              checked={filters.conditions.includes(option.id)}
              onChange={() => toggleCondition(option.id)}
              label={t(option.labelKey, option.labelDefault)}
              data-testid={`reuse-filter-condition-${option.id.toLowerCase()}`}
              data-cy={`reuse-filter-condition-${option.id.toLowerCase()}`}
            />
          ))}
        </Flex>
      </SFilterSection>

      <SFilterSection>
        <Text type={'base'} bold>
          {t('catalog.reuse_availabilities.filters.color', 'Vehicle Color')}
        </Text>
        <Flex direction={'column'} gap={8}>
          {COLOR_OPTIONS.map((option) => (
            <SColorRow key={option.id}>
              <SColorLabel>
                <SColorSwatch $color={option.code} />
                <Text type={'base'}>{t(option.labelKey, option.labelDefault)}</Text>
              </SColorLabel>
              <Checkbox
                checked={filters.colors.includes(option.code)}
                onChange={() => toggleColor(option.code)}
                data-testid={`reuse-filter-color-${option.id.toLowerCase()}`}
                data-cy={`reuse-filter-color-${option.id.toLowerCase()}`}
              />
            </SColorRow>
          ))}
        </Flex>
      </SFilterSection>
    </SFiltersColumn>
  );
};

export default ReuseAvailabilitiesFilters;
